import React, { useState, useEffect, useRef } from "react";
import { ref, update, set, onValue, remove, get } from 'firebase/database';
import { database } from '../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { addFooterToEmail, convertToHtml } from '../EmailFooterGenerator';
import { resolveFooterForSend } from './resolveFooterForSend';
import { withResolvedImageAltText, buildImagePromptVariables } from './dynamicImageUtils';
import BulkModeProgress from './BulkModeComponents/BulkModeProgress';
import BulkGenerationLoading from './BulkModeComponents/BulkGenerationLoading';
import { 
  generateEmailForContact, 
  updateContactStatus,
  saveLastSenderToDatabase 
} from './BulkModeComponents/bulkModeHelpers';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../../utils/cloudFunctions';

const DraftBulkMode = ({ 
  onClose, 
  userId, 
  onEmailResult,
  userSettings: initialUserSettings,
  aiSettings,
  userTopics,
  isTopicsLoaded,
  senderEmails,
  topicTypeMapping,
  bulkDrafts, // Array von Draft-IDs
  initialDraftIndex = 0 // Neuer Parameter für den initialen Index
}) => {
  // Grundlegende E-Mail-States
  const [selectedTopic, setSelectedTopic] = useState("Reply To Mail");
  // Style removed — defined in template
  const [contextInfo, setContextInfo] = useState("");
  const [subjectLine, setSubjectLine] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [selectedEmail, setSelectedEmail] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [selectedSenderEmail, setSelectedSenderEmail] = useState("");
  
  // Lokale userSettings mit Footer-Daten
  const [userSettings, setUserSettings] = useState(initialUserSettings || {});
  
  // Draft-Bulk-Modus-spezifische States
  const [currentDraftIndex, setCurrentDraftIndex] = useState(initialDraftIndex);
  const [currentDraft, setCurrentDraft] = useState(null);
  const [sentDrafts, setSentDrafts] = useState({});
  const [updatedBulkDrafts, setUpdatedBulkDrafts] = useState(bulkDrafts);
  
  // Ref für das Betreff-Input-Element
  const subjectInputRef = useRef(null);
  
  // State für Session-Statistiken
  const [sessionStats, setSessionStats] = useState({
    sent: 0,
    skipped: 0,
    deleted: 0
  });
  
  // Refs für akkurate Zählung
  const sessionDeletedRef = useRef(0);
  const sessionSentRef = useRef(0);
  const sessionSkippedRef = useRef(0);
  
  // Firebase Functions
  const functions = getFunctions(undefined, 'europe-west1');
  const markEmailAsReplied = httpsCallable(functions, 'markEmailAsReplied');
  
  // Initialisiere die aktualisierte Draft-Liste und setze den Index
  useEffect(() => {
    setUpdatedBulkDrafts(bulkDrafts);
    setCurrentDraftIndex(initialDraftIndex);
  }, [bulkDrafts, initialDraftIndex]);

  // Lade den aktuellen Draft
  useEffect(() => {
    if (updatedBulkDrafts.length > 0) {
      const draft = updatedBulkDrafts[currentDraftIndex];
      if (draft) {
        setCurrentDraft(draft);
      }
    }
  }, [updatedBulkDrafts, currentDraftIndex]);
  
  // Initialisiere die Absender-E-Mail als Fallback
  useEffect(() => {
    if (senderEmails.length > 0 && !selectedSenderEmail) {
      // Setze den ersten verfügbaren Absender nur als Fallback
      setSelectedSenderEmail(senderEmails[0].email);
    }
  }, [senderEmails, selectedSenderEmail]);

  // Aktualisiere den Kontext und die E-Mail-Daten, wenn sich der Draft ändert
  useEffect(() => {
    if (currentDraft) {
      // Setze den Draft-Inhalt
      setBodyContent(currentDraft.draftContent || "");
      
      // Setze den Betreff
      const originalSubject = currentDraft.originalEmail?.subject || '';
      const replySubject = originalSubject.startsWith('Re:') 
        ? originalSubject 
        : `Re: ${originalSubject}`;
      updateSubjectInputDirectly(replySubject);
      
      // Setze die E-Mail-Adresse des Empfängers
      if (currentDraft.originalEmail?.from) {
        const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
        const match = currentDraft.originalEmail.from.match(emailRegex);
        if (match && match[1]) {
          setSelectedEmail(match[1]);
        }
      }
      
      // Setze die Sender-E-Mail basierend auf der ursprünglichen E-Mail-Adresse
      if (currentDraft.originalEmail && senderEmails.length > 0) {
        const originalAccountEmail = currentDraft.originalEmail.accountEmail;
        if (originalAccountEmail) {
          const matchingEmail = senderEmails.find(email => email.email === originalAccountEmail);
          if (matchingEmail) {
            setSelectedSenderEmail(matchingEmail.email);
          } else if (senderEmails.length > 0) {
            // Fallback zum ersten verfügbaren Sender
            setSelectedSenderEmail(senderEmails[0].email);
          }
        } else if (senderEmails.length > 0) {
          // Fallback zum ersten verfügbaren Sender wenn keine Account-E-Mail gefunden
          setSelectedSenderEmail(senderEmails[0].email);
        }
      }
      
      // Setze den Kontext mit der Original-E-Mail-Historie
      const emailHistory = `
Original E-Mail:
Von: ${currentDraft.originalEmail?.from || 'Unbekannt'}
Datum: ${new Date(currentDraft.originalEmail?.date || Date.now()).toLocaleString()}
Betreff: ${currentDraft.originalEmail?.subject || ''}

${currentDraft.originalEmail?.content || currentDraft.originalEmail?.bodyText || ''}

Draft erstellt von Agent: ${currentDraft.agentName} (Case ${currentDraft.agentCaseNumber})
Draft-Aufgabe: ${currentDraft.agentTask || 'Keine Aufgabe definiert'}
      `.trim();
      
      setContextInfo(emailHistory);
      
      // Setze den Sending-Status zurück beim Draft-Wechsel
      setSendSuccess(false);
      setIsSending(false);
    }
  }, [currentDraft, senderEmails]);

  // Escape-Taste zum Schließen des Popups
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  // Verhindere Scrollen im Hintergrund
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Funktion zum Behandeln von Overlay-Klicks
  const handleOverlayClick = (e) => {
    // Nur schließen, wenn direkt auf das Overlay geklickt wurde
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Modifizierte onClose-Funktion für Statistik-Toast
  const handleClose = () => {
    // Nimm die Werte aus den Refs für die aktuellsten Zahlen
    const sent = sessionSentRef.current;
    const skipped = sessionSkippedRef.current;
    const deleted = sessionDeletedRef.current;
    
    // Zeige Toast mit Statistiken
    if (sent > 0 || skipped > 0 || deleted > 0) {
      let message = "Draft session summary: ";
      const parts = [];
      
      if (deleted > 0) {
        parts.push(`${deleted} draft${deleted === 1 ? '' : 's'} deleted`);
      }
      
      if (sent > 0) {
        parts.push(`${sent} email${sent === 1 ? '' : 's'} sent`);
      }
      
      if (skipped > 0) {
        parts.push(`${skipped} skipped`);
      }
      
      if (sent === 0 && deleted > 0) {
        message = "Drafts deleted: " + deleted;
      } else {
        message += parts.join(', ');
      }
      
      // Zeige den Toast an
      if (onEmailResult) {
        onEmailResult('summary', message);
      }
    }
    
    // Statistiken zurücksetzen
    sessionSentRef.current = 0;
    sessionSkippedRef.current = 0;
    sessionDeletedRef.current = 0;
    setSessionStats({
      sent: 0,
      skipped: 0,
      deleted: 0
    });
    
    // Originale onClose-Funktion aufrufen
    onClose();
  };

  // Draft aus der Datenbank löschen
  const handleDeleteDraft = async (draftId) => {
    if (!userId || !draftId) {
      console.error("📝 [DELETE-DRAFT] Missing userId or draftId");
      return;
    }
    
    try {
      console.log(`📝 [DELETE-DRAFT] Attempting to delete draft ${draftId}`);
      
      // Speichere den aktuellen Index vor dem Löschen
      const currentPos = currentDraftIndex;
      
      // Referenz auf den Draft in der Datenbank
      const draftRef = ref(database, `/World/${userId}/emailDrafts/${draftId}`);
      
      // Prüfe zuerst, ob der Draft noch existiert
      const draftSnapshot = await get(draftRef);
      if (!draftSnapshot.exists()) {
        console.log(`📝 [DELETE-DRAFT] Draft ${draftId} already deleted`);
        // Aktualisiere trotzdem die lokale Liste
        setUpdatedBulkDrafts(prev => prev.filter(draft => draft.id !== draftId));
        return;
      }
      
      // Draft aus der Datenbank löschen
      await remove(draftRef);
      console.log(`📝 [DELETE-DRAFT] Successfully deleted draft ${draftId} from database`);
      
      // Zähler erhöhen
      sessionDeletedRef.current += 1;
      setSessionStats(prev => ({
        ...prev,
        deleted: sessionDeletedRef.current
      }));
      
      // Aktualisiere die lokale Liste der Bulk-Drafts
      setUpdatedBulkDrafts(prevDrafts => {
        const newDrafts = prevDrafts.filter(draft => draft.id !== draftId);
        console.log(`📝 [DELETE-DRAFT] Updated local drafts list:`, {
          previousCount: prevDrafts.length,
          newCount: newDrafts.length,
          deletedDraftId: draftId
        });
        return newDrafts;
      });
      
      // Warte kurz, um sicherzustellen, dass der State aktualisiert wurde
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Hole die aktualisierten Drafts
      const remainingDrafts = updatedBulkDrafts.filter(draft => draft.id !== draftId);
      
      // Wenn keine Drafts mehr übrig sind, schließe den Bulk-Modus
      if (remainingDrafts.length === 0) {
        console.log(`📝 [DELETE-DRAFT] No drafts remaining, closing bulk mode`);
        handleClose();
        return;
      }
      
      // Prüfe, ob alle verbliebenen Drafts bereits gesendet wurden
      const allRemainingSent = remainingDrafts.every(draft => sentDrafts[draft.id]);
      if (allRemainingSent) {
        console.log(`📝 [DELETE-DRAFT] All remaining drafts already sent, closing bulk mode`);
        handleClose();
        return;
      }
      
      // Finde den nächsten ungesendeten Draft
      let nextUnsent = -1;
      for (let i = 0; i < remainingDrafts.length; i++) {
        if (!sentDrafts[remainingDrafts[i].id]) {
          nextUnsent = i;
          break;
        }
      }
      
      // Setze den Index auf den nächsten ungesendeten Draft oder den letzten verfügbaren
      if (nextUnsent !== -1) {
        console.log(`📝 [DELETE-DRAFT] Moving to next unsent draft at index ${nextUnsent}`);
        setCurrentDraftIndex(nextUnsent);
      } else {
        const newIndex = Math.min(currentPos, remainingDrafts.length - 1);
        console.log(`📝 [DELETE-DRAFT] Moving to draft at index ${newIndex}`);
        setCurrentDraftIndex(newIndex);
      }
      
    } catch (error) {
      console.error("📝 [DELETE-DRAFT] Error deleting draft:", error);
    }
  };

  // Zum nächsten Draft wechseln
  const goToNextDraft = () => {
    if (currentDraftIndex < updatedBulkDrafts.length - 1) {
      // Status explizit zurücksetzen
      setSendSuccess(false);
      setIsSending(false);
      
      // Index aktualisieren
      const nextIndex = currentDraftIndex + 1;
      setCurrentDraftIndex(nextIndex);
    }
  };
  
  // Zum vorherigen Draft wechseln
  const goToPreviousDraft = () => {
    if (currentDraftIndex > 0) {
      // Prüfe, ob der vorherige Draft bereits gesendet wurde
      const prevDraft = updatedBulkDrafts[currentDraftIndex - 1];
      
      // Wenn die E-Mail bereits gesendet wurde, nicht zurückspringen
      if (sentDrafts[prevDraft.id]) {
        return;
      }
      
      // Status explizit zurücksetzen
      setSendSuccess(false);
      setIsSending(false);
      
      // Index aktualisieren
      const prevIndex = currentDraftIndex - 1;
      setCurrentDraftIndex(prevIndex);
    }
  };
  
  // Prüfe, ob zu einem Draft zurückgesprungen werden kann
  const canGoToPrevious = () => {
    if (currentDraftIndex <= 0) return false;
    
    // Prüfe, ob der vorherige Draft bereits gesendet wurde
    const prevDraft = updatedBulkDrafts[currentDraftIndex - 1];
    return !sentDrafts[prevDraft.id];
  };

  // Direktes Aktualisieren des Betreffs
  const updateSubjectInputDirectly = (subject) => {
    setSubjectLine(subject);
    
    if (subjectInputRef.current) {
      subjectInputRef.current.value = subject;
      
      const event = new Event('input', { bubbles: true });
      subjectInputRef.current.dispatchEvent(event);
    }
  };

  // E-Mail senden und sofort zum nächsten Draft weitergehen
  const handleSend = async () => {
    if (!currentDraft || !selectedEmail) {
      console.error("No email address found for this draft");
      onEmailResult('error', 'No email address found for this draft');
      return;
    }

    const currentId = currentDraft.id;
    const currentContent = bodyContent;
    const currentSubject = subjectLine;
    const currentSelectedEmail = selectedEmail;
    const currentSenderEmail = selectedSenderEmail;
    const recipientName = currentDraft.originalEmail?.from || 'Unknown';

    setIsSending(true);
    
    // SOFORT den Draft aus der EmailDraftsPreview entfernen (optimistische UI-Aktualisierung)
    if (window.emailDraftsPreview && window.emailDraftsPreview.markDraftAsSending) {
      window.emailDraftsPreview.markDraftAsSending(currentId);
    }
    
    // SOFORT den Draft-Status auf 'sent' setzen und aus der Datenbank entfernen
    try {
      const draftRef = ref(database, `/World/${userId}/emailDrafts/${currentId}`);
      
      // Setze den Status auf 'sent' und entferne den Draft sofort
      await update(draftRef, {
        status: 'sent',
        sentAt: new Date().toISOString(),
        sentTimestamp: Date.now(),
        sentTo: currentSelectedEmail,
        sentSubject: currentSubject
      });
      
      console.log(`📝 [DRAFT-SENT] Draft ${currentId} immediately marked as sent`);
      
      // Entferne den Draft sofort aus der Datenbank
      await remove(draftRef);
      console.log(`📝 [DRAFT-REMOVED] Draft ${currentId} immediately removed from database`);
      
    } catch (draftError) {
      console.error(`📝 [DRAFT-ERROR] Failed to update/remove draft immediately:`, draftError);
    }
    
    // Optimistische UI-Aktualisierung
    setSendSuccess(true);
    
    // Statistik für gesendete E-Mails erhöhen
    sessionSentRef.current += 1;
    setSessionStats(prev => ({
      ...prev,
      sent: sessionSentRef.current
    }));
    
    // Markiere diesen Draft als gesendet (für lokale UI)
    setSentDrafts(prev => ({
      ...prev,
      [currentId]: {
        timestamp: Date.now()
      }
    }));
    
    // Optimistischer Statuswechsel zum nächsten Draft!
    if (currentDraftIndex < updatedBulkDrafts.length - 1) {
      // Sofort zum nächsten Draft wechseln!
      setTimeout(() => {
        goToNextDraft();
        setIsSending(false);
      }, 300);
    } else {
      // Bei dem letzten Draft etwas warten und dann schließen
      setTimeout(() => {
        handleClose();
      }, 1000);
    }
    
    // Callback für EmailResult
    if (onEmailResult) {
      onEmailResult('success', `Sending email to ${recipientName}...`, currentDraft);
    }

    // --- VEREINFACHTE REPLY-SCHEDULING-LOGIK ---
    (async () => {
      try {
        const senderEmailData = senderEmails.find(item => item.email === currentSenderEmail);
        if (!senderEmailData) throw new Error("The selected sender email was not found.");

        // Resolve gegen stale React-State (Race zwischen Firestore/RTDB-Listenern).
        // Bei Read-Error fällt der Helper transparent auf den bestehenden State zurück.
        const { footerId: resolvedFooterId, footerSettings: resolvedFooterSettings } = await resolveFooterForSend({
          userId,
          senderAccountId: senderEmailData.id,
          fallbackFooterId: senderEmailData.footerId || null,
          fallbackFooterSettings: userSettings,
        });

        // E-Mail-Inhalt formatieren. Alt-Texte pro Empfänger auflösen (verhindert
        // identische / platzhalter-leakende alt-Attribute). Hinweis: im Draft-
        // Reply-Flow liegen oft keine echten Firmendaten vor → dann werden
        // erkannte Platzhalter zu leeren Strings aufgelöst statt geleakt.
        const settingsForHtml = withResolvedImageAltText(
          resolvedFooterSettings,
          buildImagePromptVariables(currentDraft, { userSettings, selectedEmail: currentSelectedEmail })
        );
        const formattedContent = convertToHtml(currentContent, settingsForHtml, resolvedFooterId);
        let contentWithFooter = addFooterToEmail(formattedContent, resolvedFooterSettings, resolvedFooterId);
        
        // E-Mail-Historie anhängen falls vorhanden
        if (currentDraft.originalEmail) {
          const originalEmail = currentDraft.originalEmail;
          const emailHistory = `\n<div style=\"margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; color: #666;\">\n  <p style=\"margin-bottom: 10px; color: #888; font-style: italic;\">Am ${new Date(originalEmail.date || Date.now()).toLocaleString()} schrieb ${originalEmail.from || 'Unbekannt'}:</p>\n  <blockquote style=\"margin: 0; padding-left: 10px; border-left: 2px solid #ddd; color: #666;\">\n    ${originalEmail.content || originalEmail.bodyText || ''}\n  </blockquote>\n</div>\n        `;
          contentWithFooter = `${contentWithFooter}\n\n${emailHistory}`;
        }
        
        // Request-Daten für die Cloud Function (OHNE client-seitige Delay-Berechnung)
        const requestData = {
          recipients: [currentSelectedEmail],
          subject: currentSubject || '',
          content: contentWithFooter || '',
          userId: userId,
          businessName: "E-Mail-Antwort (Draft)",
          leadId: currentId,
          emailType: "draft_reply",
          senderEmail: senderEmailData.email.trim(),
          senderPassword: senderEmailData.password,
          senderName: senderEmailData.sender_name || userSettings.user_name || 'BonusClub',
          imapHost: senderEmailData.imap_host,
          imapPort: senderEmailData.imap_port,
          smtpHost: senderEmailData.smtp_host,
          smtpPort: senderEmailData.smtp_port,
          encryptionType: senderEmailData.encryption_type,
          smtpEncryption: String(senderEmailData.smtp_encryption),
          emailId: senderEmailData.id,
          isReply: true, // WICHTIG: Markiere als Reply
          queueName: 'reply-queue' // WICHTIG: Reply-Queue verwenden
          // scheduledTime wird von der Cloud Function berechnet
        };
        
        // Sende an die Cloud Function (Delay-Berechnung erfolgt dort)
        const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.sendWorldEmail(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestData),
        });
        
        const responseText = await response.text();
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (e) {
          if (onEmailResult) onEmailResult('error', `Server response is not valid JSON: ${responseText.substring(0, 100)}...`);
          return;
        }
        
        if (data.success) {
          // SafeGuard or Pre-Send Scorer rejected → saved as draft
          if (data.drafted) {
            const reason = data.preSendScore
              ? `Pre-Send Score: ${data.preSendScore.compositeScore}/10 — saved as draft`
              : `SafeGuard: ${data.safeGuardRating}/${data.minimumRating || 10} — saved as draft`;
            if (onEmailResult) {
              onEmailResult('warning', reason, currentDraft);
            }
            return;
          }

          if (onEmailResult) {
            if (data.delayed && data.scheduledTime) {
              let scheduledTimeStamp = null;
              try { 
                scheduledTimeStamp = new Date(data.scheduledTime).getTime(); 
              } catch (e) {
                console.error("Error converting scheduled time:", e);
              }
              onEmailResult('success', `Reply scheduled for later`, currentDraft, scheduledTimeStamp, currentSelectedEmail, currentSenderEmail);
            } else {
              onEmailResult('success', `Reply successfully sent`, currentDraft, null, currentSelectedEmail, currentSenderEmail);
            }
          }
          await saveLastSenderToDatabase(currentSenderEmail, userId);
          await markOriginalEmailAsReplied(currentDraft);
        } else {
          if (data.optOutBlocked) {
            if (onEmailResult) onEmailResult('blocked', `Contact is blocked or opted out. ${data.error || ''}`, currentDraft);
            return;
          }
          // Bei Fehlern
          let errorMessage = data.error || 'Unknown error sending email';
          if (data.error && data.error.includes("Sender address rejected")) {
            errorMessage = `The email address \"${senderEmailData.email}\" was rejected by the server.`;
          }
          if (onEmailResult) onEmailResult('error', errorMessage, currentDraft);
        }
      } catch (error) {
        if (onEmailResult) onEmailResult('error', error.message || 'Unknown error sending email', currentDraft);
      }
    })();
  };

  // Funktion zum Markieren der ursprünglichen E-Mail als beantwortet
  const markOriginalEmailAsReplied = async (draft) => {
    if (!draft.originalEmailId || !userId) {
      console.warn(`📝 [MARK-REPLIED] Missing originalEmailId or userId for draft ${draft.id}`);
      return;
    }
    
    try {
      console.log(`📝 [MARK-REPLIED] Marking original email ${draft.originalEmailId} as replied`);
      
      // ✅ NEU: Rufe NUR die Cloud-Funktion auf (kein RTDB Update mehr)
      // Die Cloud-Funktion markiert die E-Mail in Firestore als beantwortet
      
      // Rufe die Cloud-Funktion auf, um den Status in Firestore und auf dem E-Mail-Server zu aktualisieren
      await markEmailAsReplied({ 
        emailId: draft.originalEmailId, 
        accountId: draft.originalEmail?.accountId,
        isSpam: draft.originalEmailPath === 'spam',
        userId: userId // 🔒 TEAM SUPPORT: Übergebe userId für Team-Mitglieder
      });
      
      console.log(`📝 [MARK-REPLIED] Successfully marked email ${draft.originalEmailId} as replied`);
      
    } catch (error) {
      console.error(`📝 [MARK-REPLIED] Error marking email ${draft.originalEmailId} as replied:`, error);
      // Fehler nicht weiterwerfen, da die E-Mail trotzdem erfolgreich gesendet wurde
    }
  };

  // Prüfen, ob alle erforderlichen Felder ausgefüllt sind
  const isEmailValid = () => {
    return (
      selectedEmail && 
      selectedEmail.trim() !== '' && 
      selectedSenderEmail && 
      selectedSenderEmail.trim() !== '' && 
      subjectLine && 
      subjectLine.trim() !== '' && 
      bodyContent && 
      bodyContent.trim() !== ''
    );
  };

  // Erstelle ein "Kontakt"-Objekt für die BulkModeProgress Komponente
  const getCurrentDraftAsContact = () => {
    if (!currentDraft) return null;
    
    return {
      id: currentDraft.id,
      business_name: `Draft from ${currentDraft.agentName}`,
      lead_name: currentDraft.originalEmail?.from || 'Unknown'
    };
  };

  // Lade Footer-Einstellungen direkt (wie in EmailAiPopup.js)
  useEffect(() => {
    if (!userId) return;
    
    // Initialisiere mit den übergebenen userSettings
    setUserSettings(initialUserSettings || {});
    
    // Lade Footer-Einstellungen separat
    const footerSettingsRef = ref(database, `/World/${userId}/settings/footer`);
    const unsubscribeFooter = onValue(footerSettingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const footerSettings = snapshot.val();
        
        // Prüfe ob Multi-Footer-System aktiv ist
        const useMultipleFooters = footerSettings.useMultipleFooters === true;
        const footerList = Array.isArray(footerSettings.footerList) ? footerSettings.footerList : [];
        
        if (useMultipleFooters && footerList.length > 0) {
          const firstActive = footerList.find(f => f && f.isActive !== false) || footerList[0];
          setUserSettings(prev => ({
            ...prev,
            useMultipleFooters: true,
            footerList: footerList,
            footer_font: firstActive?.footer_font || "Helvetica Neue"
          }));
        } else {
          // Legacy Single-Footer-System: Lade alte Einstellungen
          setUserSettings(prev => ({
            ...prev,
            footer_text: footerSettings.footer_text || "",
            footer_image_url: footerSettings.footer_image_url || "",
            footer_image_position: footerSettings.footer_image_position || "below",
            footer_font: footerSettings.footer_font || "Helvetica Neue",
            footer_image_size: footerSettings.footer_image_size || 80,
            socialIcons: footerSettings.socialIcons || [
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 },
              { iconUrl: "", link: "", size: 32 }
            ],
            socialIconsPosition: footerSettings.socialIconsPosition || "left",
            socialIconsMarginTop: footerSettings.socialIconsMarginTop !== undefined ? footerSettings.socialIconsMarginTop : 15,
            socialIconsMarginBottom: footerSettings.socialIconsMarginBottom !== undefined ? footerSettings.socialIconsMarginBottom : 10,
            useMultipleFooters: false,
            footerList: []
          }));
        }
      }
    });
    
    return () => {
      unsubscribeFooter();
    };
  }, [userId, initialUserSettings]);

  return (
    <div className="emailAiPopupOverlay" onClick={handleOverlayClick}>
      <div className="emailAiPopupContainer bulk-mode" onClick={(e) => e.stopPropagation()}>
        <div className="emailAiPopupHeader">
          <h2>📝 Draft Bulk Manager</h2>
          <button className="emailAiPopupCloseButton" onClick={handleClose}>×</button>
        </div>
        
        <BulkModeProgress 
          currentBulkIndex={currentDraftIndex}
          totalContacts={updatedBulkDrafts.length}
          goToPreviousBulkContact={goToPreviousDraft}
          goToNextBulkContact={goToNextDraft}
          currentBulkContact={getCurrentDraftAsContact()}
          sentEmails={sentDrafts}
          canGoToPrevious={canGoToPrevious}
          bulkContacts={updatedBulkDrafts.map(draft => draft.id)}
          onDeleteContact={handleDeleteDraft}
        />
        
        <div className="emailAiPopupContent">
          {/* LINKE SPALTE: Context und Draft-Info */}
          <div className="emailAiPopupLeftColumn">
            <div className="emailAiPopupTopicSection">
              <h3 className="emailAiPopupSectionTitle">Draft Info</h3>
              {currentDraft && (
                <div className="draft-info-container">
                  <div className="draft-info-item">
                    <strong>Agent:</strong> {currentDraft.agentName} (Case {currentDraft.agentCaseNumber})
                  </div>
                  <div className="draft-info-item">
                    <strong>Created:</strong> {new Date(currentDraft.createdAt).toLocaleString()}
                  </div>
                </div>
              )}
            </div>

            <div className="emailAiPopupContextSection">
              <h3 className="emailAiPopupSectionTitle">Email History</h3>
              <div className="emailAiPopupContextWrapper">
                <textarea
                  className="emailAiPopupContextInput"
                  value={contextInfo}
                  onChange={(e) => setContextInfo(e.target.value)}
                  readOnly
                ></textarea>
              </div>
            </div>
          </div>

          {/* RECHTE SPALTE: Subject, Body, Send */}
          <div className="emailAiPopupRight">
            {/* Absender-E-Mail-Auswahl */}
            <div className="emailAiPopupField">
              <h3 className="emailAiPopupSectionTitle">From</h3>
              <div className="emailAiPopupSenderEmailsContainer">
                {senderEmails.length > 0 ? (
                  <div className="emailAiPopupSenderEmailsScroll">
                    {senderEmails.map((item) => (
                      <button 
                        key={item.email}
                        className={`emailAiPopupSenderEmailButton ${selectedSenderEmail === item.email ? 'active' : ''}`}
                        onClick={() => setSelectedSenderEmail(item.email)}
                      >
                        {item.sender_name 
                          ? `${item.sender_name} <${item.email}>` 
                          : item.email}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="emailAiPopupNoEmails">No sender emails configured.</p>
                )}
              </div>
            </div>

            {/* Empfänger-E-Mail-Anzeige */}
            <div className="emailAiPopupRecipientSection">
              <h3 className="emailAiPopupSectionTitle">To</h3>
              <div className="emailAiPopupTopButtons">
                <div className="emailAiPopupButtonWrapper">
                  <button className="emailAiPopupButton active">
                    {selectedEmail}
                  </button>
                </div>
              </div>
            </div>

            {/* Betreff und Inhalt */}
            <input
              className="emailAiPopupSubjectInput"
              placeholder="Subject line"
              value={subjectLine}
              onChange={(e) => setSubjectLine(e.target.value)}
              ref={subjectInputRef}
            />

            <div className="emailAiPopupTexfieldwrapper">
              <textarea
                id="emailContent"
                className="emailAiPopupTextArea"
                value={bodyContent}
                onChange={(e) => setBodyContent(e.target.value)}
                placeholder="Draft content..."
              ></textarea>
            </div>
            
            <div className="emailAiPopupBottomButtons">
              <button
                className={`emailAiPopupSendButton ${sendSuccess ? 'success' : ''} ${isSending ? 'sending' : ''} ${!isEmailValid() ? 'disabled' : ''}`}
                onClick={handleSend}
                disabled={isSending || !isEmailValid()}
              >
                {isSending ? (
                  <>
                    <div className="emailAiPopupSpinner"></div>
                    <span>Sending...</span>
                  </>
                ) : sendSuccess ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>Sent</span>
                  </>
                ) : (
                  <>
                    <span>{currentDraftIndex === updatedBulkDrafts.length - 1 ? 'Send & Finish' : 'Send & Next'}</span>
                    <img src="/img/sender.svg" alt="Send" className="emailAiPopupSendIcon" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DraftBulkMode; 